import React, { useEffect, useState } from "react";
import styles from './EvaluationSummary.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import EvaluationSummaryCard from "../../../components/evaluationSummaryCard/EvaluationSummaryCard";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import Button from "../../../components/button/Button";
import Modal from "../../../components/modal/Modal";
import ApproveInstructorForm from "../../../components/approveInstructorForm/ApproveInstructorForm";
import Icon from "../../../components/icon/Icon";

const EvaluationSummary = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [openCard, setOpenCard] = useState(0);
  const [score, setScore] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isTemporaryInstructor, setIsTemporaryInstructor] = useState(false);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [instructor, setInstructor] = useState({});
  const [isNoteVisible, setIsNoteVisible] = useState(false);
  const [note, setNote] = useState('');
  const [enableActions, setEnableActions] = useState(false);
  const { evaluationId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await axiosClient.get(`evaluations/summary/${evaluationId}`)
      .then((response) => {
        if(response.data) {
          setScore(response.data?.overallScore);
          setQuestions(response.data?.questions);
          setIsTemporaryInstructor(response.data?.isTemporaryInstructor);
          setInstructor(response.data?.instructor);
          setNote(response.data?.note);

          if(response.data?.isCreatedByStudent && response.data?.approvalStatus === 'Pending') {
            setEnableActions(true);
          }
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, [evaluationId, showToast]);

  const handleUpdate = async (formData) => {
    setIsModalLoading(true);
    await axiosClient.patch(`evaluations/by-students`, formData)
    .then((response) => {
      showToast(response.message);
      navigate(`/evaluations`);
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsModalLoading(false);
    });
  }

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Evaluations', path: '/evaluations' },
            { label: `Evaluation ID - ${evaluationId}` }
          ]}
        />
        {enableActions && <div className={styles.cta}>
          <Button
            label="Reject"
            size="small"
            variant="white"
            onClick={() => setShowRejectModal(true)}
          />
          <Button
            label="Approve"
            onClick={() => {
              setShowApproveModal(true);
            }}
            size="small"
          />
        </div>}
      </div>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.left}>
            <h1>Summary</h1>
            <button 
              onClick={() => setIsNoteVisible(!isNoteVisible)}
              className={styles.noteButton}
            >
              <Icon name="clipboard" />Confidential Notes
            </button>
            {isNoteVisible && <Modal>
              <div className={styles.confidentialNotes}>
                <h5>Confidential Notes</h5>
                <p>{ note ?? 'Notes not available' }</p>
                <div className={styles.footer}>
                  <div className={styles.help}>
                    <Icon name="lock" />
                    <div className={styles.text}>
                      <label>Private </label>
                      <span>Only visible to the director / program admin</span>
                    </div>
                  </div>
                  <Button
                    label="Close"
                    variant="black"
                    size="small"
                    onClick={() => setIsNoteVisible(false)}
                  />
                </div>
              </div>       
            </Modal>}
          </div>
          <div className={styles.right}>
            <div className={styles.scoreBox}>
              Overall Score <strong>{score}</strong>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.sectionContent}>
          {questions.map((question) => 
            <EvaluationSummaryCard
              key={`question-${question.id}`}
              isOpened={openCard === question.id}
              onClick={() => setOpenCard(openCard !== question.id ? question.id : 0)}
              data={question}
            />)}
        </div>
      </div>
      {showApproveModal && (
        <Modal>
          <div className={styles.modal}>
            {isModalLoading && <LoadingOverlay />}
            <h2 className={styles.modalTitle}>
              {
                isTemporaryInstructor ? 
                  'Instructor not approved. Do you want to approve this instructor?' :
                  'Are you sure you want to approve?'
              }
            </h2>
            <div className={styles.buttons}>
              <Button
                label="Cancel"
                size="small"
                variant="secondary"
                onClick={() => setShowApproveModal(false)}
              />
              <Button
                label={isTemporaryInstructor ? 'Proceed' : 'Approve'}
                size="small"
                onClick={() => {
                  if(isTemporaryInstructor) {
                    setShowInstructorModal(true);
                  } else {
                    handleUpdate({
                      evaluationId: evaluationId,
                      status: 'Approve',
                    })
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
      {showRejectModal && (
        <Modal>
          <div className={styles.modal}>
            {isModalLoading && <LoadingOverlay />}
            <h2 className={styles.modalTitle}>Are you sure you want to reject?</h2>
            <div className={styles.buttons}>
              <Button
                label="Cancel"
                size="small"
                variant="secondary"
                onClick={() => setShowRejectModal(false)}
              />
              <Button
                label="Reject"
                size="small"
                onClick={() => handleUpdate({
                  evaluationId: evaluationId,
                  status: 'Reject'
                })}
              />
            </div>
          </div>
        </Modal>
      )}
      {showInstructorModal && <Modal scrollable={false}>
        <ApproveInstructorForm
          instructor={instructor}
          onCancel={() => setShowInstructorModal(false)}
          onSubmit={(data) => {
            handleUpdate({
              evaluationId: evaluationId,
              status: 'Approve',
              ...data
            })
          }}
          isLoading={isModalLoading}
        />
      </Modal>}
    </div>
  );
};

export default EvaluationSummary;